import React, { useState, useEffect } from "react";

import { Form, Formik } from "formik";
import * as yup from "yup";

import providerManagerAssociationsApi from "apis/providerManagerAssociation";
import providerManagerDashboardApi from "apis/providerManagerDashboard";
import { Button, Typography, Switch } from "clearsense-ui/components";
import {
  Select,
  Input,
  DatePicker,
  Textarea,
} from "clearsense-ui/components/formik";

import { CREATE_ASSOCIATION_INITIAL_VALUES } from "../constants";

const AddAssociation = ({
  setIsAddingAssociation,
  handleAddAssociation,
  activeTab,
  associationToEdit,
  setAssociationToEdit,
  associationTemplate,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [entityItemOptions, setEntityItemOptions] = useState([]);
  const [editInitializeValue, setEditInitializeValue] = useState(null);

  const fetchCurrentEntityItems = async () => {
    setIsLoading(true);
    try {
      const { data } =
        await providerManagerDashboardApi.fetchSpecificEntityItems(
          activeTab.id
        );
      setEntityItemOptions(data.entity_items);
    } catch (err) {
      logger.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssociationDetails = async () => {
    try {
      const { data } = await providerManagerAssociationsApi.show(
        associationToEdit
      );
      setEditInitializeValue({
        target_key: {
          label: data.target_entity_item_key,
          value: data.target_entity_item_id,
        },
        association_type: data.association_type,
        start_date: data.start_date,
        end_date: data.end_date,
        ...data.template_fields_data,
      });
    } catch (err) {
      logger.log(err);
    }
  };

  useEffect(() => {
    fetchCurrentEntityItems();
  }, []);

  useEffect(() => {
    fetchCurrentEntityItems();
    associationToEdit && fetchAssociationDetails();
  }, [associationToEdit]);

  const generateTemplateFields = (field, formik) => {
    const { type, name } = field;

    switch (type) {
      case "text":
      case "string":
      case "number":
        return <Input key={name} label={name} name={name} />;
      case "textarea":
        return <Textarea key={name} label={name} name={name} />;
      case "date":
        return (
          <DatePicker
            key={name}
            label={name}
            name={name}
            size="large"
            dateFormat="YYYY-MM-DD"
          />
        );
      case "switch":
      case "boolean":
        return (
          <Switch
            key={name}
            label={name}
            name={name}
            onChange={e =>
              formik.setFieldValue(name, e.target.checked ? "Y" : "N")
            }
            checked={formik.values?.[name] === "Y"}
          />
        );
      default:
        return null;
    }
  };

  const generateValidationSchema = () =>
    yup.object().shape({
      target_key: yup.object().required("Please select an target entity item"),
      start_date: yup.date().required("Please select a date"),
      end_date: yup.date().required("Please select a date"),
      ...associationTemplate?.reduce((acc, curr) => {
        const { type, name } = curr;

        switch (type) {
          case "text":
          case "string":
          case "number":
            return {
              ...acc,
              [name]: yup.string().trim().required("Please enter a value"),
            };
          case "textarea":
            return {
              ...acc,
              [name]: yup.string().trim().required("Please enter a value"),
            };
          case "date":
            return {
              ...acc,
              [name]: yup.date().required("Please select a date"),
            };
          case "switch":
          case "boolean":
            return {
              ...acc,
              [name]: yup.string().trim().required("Please enter a value"),
            };
          default:
            return null;
        }
      }, {}),
    });

  const generateInitialValues = () =>
    editInitializeValue
      ? editInitializeValue
      : {
          ...CREATE_ASSOCIATION_INITIAL_VALUES,
          ...associationTemplate?.reduce((acc, curr) => {
            if (curr?.type === "switch" || curr?.type === "boolean") {
              return { ...acc, [curr?.name]: "N" };
            }

            return { ...acc, [curr?.name]: "" };
          }, {}),
        };

  return (
    <div className="bg-white p-8">
      <Formik
        enableReinitialize
        initialValues={generateInitialValues()}
        validationSchema={generateValidationSchema()}
        onSubmit={handleAddAssociation}
      >
        {formik => (
          <Form className="space-y-4">
            <Typography style="h4" type="bold">
              {associationToEdit ? "Edit" : "Add New"} Association
            </Typography>
            <Select
              label="Target Entity Item Key"
              name="target_key"
              loading={isLoading}
              isDisabled={associationToEdit}
              placeholder="Choose target entity item key"
              options={entityItemOptions.map(option => ({
                label: option.entity_key,
                value: option.id,
              }))}
            />
            {associationTemplate?.map(field =>
              generateTemplateFields(field, formik)
            )}
            <div className="space-x-2">
              <Button type="submit" label="Save" />
              <Button
                style="secondary"
                label="Cancel"
                onClick={() => {
                  setIsAddingAssociation(false);
                  setAssociationToEdit();
                }}
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddAssociation;
